import {
  useState,
  useRef,
  useEffect,
  useContext,
  createContext,
  type ReactNode,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

// DropdownItem stops click propagation (see below) so that selecting an
// item never leaks a click into ancestor handlers (e.g. a table row that
// navigates on click). That same stopPropagation() also prevents the
// click from ever reaching the Dropdown's own wrapper div, which is what
// used to close the menu. Without this context, clicking an item left the
// dropdown open (rendered at z-[9999]) sitting on top of anything the
// click opened, such as a confirmation dialog (z-50) - which looked like
// the click did nothing. This context lets DropdownItem close the menu
// directly, independent of event bubbling.
const DropdownCloseContext = createContext<() => void>(() => {});

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  width?: string;
}

interface Position {
  top: number;
  left: number;
}

export function Dropdown({ trigger, children, align = 'right', width = 'w-80' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = dropdownRef.current?.offsetWidth || 160;

    let left: number;
    if (align === 'right') {
      left = triggerRect.right - dropdownWidth;
    } else {
      left = triggerRect.left;
    }

    // Ensure dropdown doesn't go off-screen horizontally
    const viewportWidth = window.innerWidth;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = viewportWidth - dropdownWidth - 8;
    }
    if (left < 8) {
      left = 8;
    }

    setPosition({
      top: triggerRect.bottom + 8,
      left,
    });
  }, [align]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    function handleScroll() {
      if (isOpen) {
        updatePosition();
      }
    }

    function handleResize() {
      if (isOpen) {
        updatePosition();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleItemClick = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div ref={triggerRef} onClick={handleToggle} className="cursor-pointer">
        {trigger}
      </div>

      {isOpen &&
        createPortal(
          <DropdownCloseContext.Provider value={() => setIsOpen(false)}>
            <div
              ref={dropdownRef}
              className={`fixed ${width} bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[9999] py-1`}
              style={{
                top: position.top,
                left: position.left,
              }}
              onClick={handleItemClick}
            >
              {children}
            </div>
          </DropdownCloseContext.Provider>,
          document.body,
        )}
    </>
  );
}

interface DropdownHeaderProps {
  children: ReactNode;
}

export function DropdownHeader({ children }: DropdownHeaderProps) {
  return (
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-medium text-gray-900 dark:text-gray-100">{children}</div>
  );
}

interface DropdownItemProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function DropdownItem({ children, onClick, className = '' }: DropdownItemProps) {
  const closeDropdown = useContext(DropdownCloseContext);

  return (
    <div
      className={`px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
        closeDropdown();
      }}
    >
      {children}
    </div>
  );
}

interface DropdownFooterProps {
  children: ReactNode;
}

export function DropdownFooter({ children }: DropdownFooterProps) {
  return (
    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">{children}</div>
  );
}
